import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'textarea:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) =>
      element.getAttribute('aria-hidden') !== 'true' &&
      element.getClientRects().length > 0,
  );
}

export function topmostModal(): HTMLElement | null {
  const dialogs = Array.from(
    document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]'),
  ).filter((dialog) => dialog.getClientRects().length > 0);
  let top: HTMLElement | null = null;
  let topZ = -Infinity;
  for (const dialog of dialogs) {
    const parsed = Number.parseInt(getComputedStyle(dialog).zIndex, 10);
    const z = Number.isFinite(parsed) ? parsed : 0;
    // Later DOM order wins equal-z stacking, matching normal painting order.
    if (z >= topZ) {
      top = dialog;
      topZ = z;
    }
  }
  return top;
}

function owningModal(container: HTMLElement): HTMLElement | null {
  if (container.matches('[role="dialog"][aria-modal="true"]')) return container;
  return container.closest<HTMLElement>('[role="dialog"][aria-modal="true"]');
}

function isTopmostModal(container: HTMLElement): boolean {
  return topmostModal() === owningModal(container);
}

interface DialogFocusOptions {
  open: boolean;
  active?: boolean;
  containerRef: RefObject<HTMLElement | null>;
  initialFocusRef?: RefObject<HTMLElement | null>;
  onEscape?: () => void;
}

export function useDialogFocus({
  open,
  active = true,
  containerRef,
  initialFocusRef,
  onEscape,
}: DialogFocusOptions): void {
  const openerRef = useRef<HTMLElement | null>(null);
  const dismissedAsTopmostRef = useRef(false);

  useLayoutEffect(() => {
    if (!open) return;
    dismissedAsTopmostRef.current = false;
    const container = containerRef.current;
    openerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      if (container && !isTopmostModal(container)) return;
      const target =
        initialFocusRef?.current ??
        (container ? focusableElements(container)[0] : null) ??
        container;
      target?.focus({ preventScroll: true });
    });
    return () => {
      cancelled = true;
      const opener = openerRef.current;
      const top = topmostModal();
      const modal = container ? owningModal(container) : null;
      const dismissedTopCanRestore =
        dismissedAsTopmostRef.current &&
        (top === null || top === modal || (opener !== null && top.contains(opener)));
      // An underlying dialog can unmount while a newer one owns focus. Never
      // steal focus out of that top layer. A focusable backdrop is outside the
      // container, though, so normal backdrop dismissal also restores when this
      // dialog is still topmost at cleanup time.
      if (
        opener?.isConnected &&
        container &&
        (dismissedTopCanRestore ||
          container.contains(document.activeElement) ||
          isTopmostModal(container))
      ) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [open, containerRef, initialFocusRef]);

  useEffect(() => {
    if (!open || !active) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const container = containerRef.current;
      if (!container || !isTopmostModal(container)) return;
      if (event.key === 'Escape' && onEscape) {
        event.preventDefault();
        event.stopPropagation();
        // WebKit moves focus to <body> before React runs layout-effect cleanup.
        // Remember that this dialog owned the dismiss so cleanup can still
        // restore its opener without allowing an underlying dialog to steal
        // focus when a newer layer closes above it.
        dismissedAsTopmostRef.current = true;
        onEscape();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = focusableElements(container);
      if (focusable.length === 0) {
        event.preventDefault();
        container.focus({ preventScroll: true });
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;
      if (event.shiftKey && (current === first || !container.contains(current))) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && (current === last || !container.contains(current))) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, active, containerRef, onEscape]);
}
